import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { MatchWithPlayers, MatchWithPlayersAndClub, Player, MatchResult, ClubWithMembership, AmericanoTournamentWithDetails } from "../../../shared/schema";
import { formatDistanceToNow, format, differenceInHours } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Check, Clock, TrendingUp, TrendingDown, Building2, Calendar, Search, FileEdit, X, ChevronLeft, ChevronRight, CheckCircle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Loader2 } from "lucide-react";

type HistoryFilter = "mine" | "clubs" | "all";

interface HistoryTabProps {
  playerId?: string | null;
}

export default function HistoryTab({ playerId }: HistoryTabProps) {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<HistoryFilter>(playerId ? "mine" : "all");
  const [searchQuery, setSearchQuery] = useState<string>(""); // Search by player name or club name
  const [loadedCount, setLoadedCount] = useState<number>(20); // Number of matches currently loaded
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [tournamentPages, setTournamentPages] = useState<Record<string, number>>({});
  const sentinelRef = useRef<HTMLDivElement>(null);
  const autoSwitchedRef = useRef<boolean>(false);

  // Fetch player's clubs for "Mes clubs" filter
  const { data: myClubs = [] } = useQuery<ClubWithMembership[]>({
    queryKey: ["/api/clubs", playerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/clubs?playerId=${playerId}`);
      return response.json();
    },
    enabled: !!playerId,
  });
  const myClubIds = new Set(myClubs.filter(c => c.isMember && c.status !== 'pending').map(c => c.id));

  const { data: matches = [], isLoading } = useQuery<MatchWithPlayersAndClub[]>({
    queryKey: ["/api/matches"],
    select: (data: MatchWithPlayersAndClub[]) =>
      data
        .filter(match => match.isCompleted)
        .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()),
  });

  const { data: completedTournaments = [] } = useQuery<AmericanoTournamentWithDetails[]>({
    queryKey: ["/api/americano/completed"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/americano");
      const all: AmericanoTournamentWithDetails[] = await res.json();
      // Fetch details for each completed tournament
      const completed = all.filter((t) => t.status === "completed");
      return Promise.all(
        completed.map(async (t) => {
          const r = await apiRequest("GET", `/api/americano/${t.id}`);
          return r.json();
        })
      );
    },
  });

  // Filter matches based on active tab filter
  const tabFilteredMatches = matches.filter(match => {
    if (activeFilter === "mine") {
      if (!playerId) return false;
      return (
        match.teamAPlayer1?.id === playerId ||
        match.teamAPlayer2?.id === playerId ||
        match.teamBPlayer1?.id === playerId ||
        match.teamBPlayer2?.id === playerId
      );
    }
    if (activeFilter === "clubs") {
      if (!playerId || myClubIds.size === 0) return false;
      return match.clubId ? myClubIds.has(match.clubId) : false;
    }
    return true; // "all"
  });

  // Filter Américano tournaments by tab
  const filteredTournaments = completedTournaments.filter((t) => {
    if (activeFilter === "mine") {
      if (!playerId) return false;
      return t.players.some((p) => p.playerId === playerId);
    }
    if (activeFilter === "clubs") {
      if (!playerId || myClubIds.size === 0) return false;
      return t.clubId ? myClubIds.has(t.clubId) : false;
    }
    return true;
  }).filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return t.name.toLowerCase().includes(q) ||
      t.players.some((p) => p.player?.name?.toLowerCase().includes(q));
  });

  // Filter matches based on search query (player names or club name)
  const filteredMatches = tabFilteredMatches.filter(match => {
    if (!searchQuery.trim()) return true;

    const searchGroups = searchQuery.split(/[|;,]/).map(t => t.trim().toLowerCase()).filter(t => t.length > 0);
    if (searchGroups.length === 0) return true;

    // Extract all player names
    const playerNames = [
      match.teamAPlayer1?.name,
      match.teamAPlayer2?.name,
      match.teamBPlayer1?.name,
      match.teamBPlayer2?.name
    ].filter(Boolean).map(name => name!.toLowerCase());

    const clubName = match.club?.name?.toLowerCase() || "";

    // A match satisfies if it matches ANY of the groups (OR)
    return searchGroups.some(group => {
      // Within an OR group, ALL words must be present (AND)
      const requiredWords = group.split(/[\s+]+/).filter(w => w.length > 0);

      return requiredWords.every(word =>
        playerNames.some(name => name.includes(word)) || clubName.includes(word)
      );
    });
  });

  // If "mine" filter yields no results after data loads, fall back to "all" (once only)
  useEffect(() => {
    if (!autoSwitchedRef.current && activeFilter === "mine" && !isLoading && tabFilteredMatches.length === 0 && filteredTournaments.length === 0) {
      autoSwitchedRef.current = true;
      setActiveFilter("all");
    }
  }, [isLoading, activeFilter, tabFilteredMatches.length, filteredTournaments.length]);

  // Reset loaded count when search query or filter changes
  useEffect(() => {
    setLoadedCount(20);
  }, [searchQuery, activeFilter]);

  // Infinite scroll: Load more matches when sentinel is visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && loadedCount < filteredMatches.length) {
          setIsLoadingMore(true);
          // Simulate loading delay for better UX
          setTimeout(() => {
            setLoadedCount((prev) => Math.min(prev + 20, filteredMatches.length));
            setIsLoadingMore(false);
          }, 300);
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: "100px", // Trigger 100px before reaching the bottom
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [filteredMatches.length, loadedCount, isLoadingMore]);

  // Pagination: Show loaded matches
  const ITEMS_PER_PAGE = 20;
  const paginatedMatches = filteredMatches.slice(0, loadedCount);

  // Fetch all match results at once to avoid hooks in loops
  const matchIds = paginatedMatches.map(match => match.id);
  const { data: allMatchResults = [] } = useQuery<MatchResult[]>({
    queryKey: ["/api/matches/results/bulk", matchIds],
    enabled: matchIds.length > 0,
    queryFn: async () => {
      if (matchIds.length === 0) return [];

      const params = new URLSearchParams();
      matchIds.forEach(id => params.append('matchIds', id));

      const response = await apiRequest("GET", `/api/matches/results/bulk?${params.toString()}`);
      return response.json();
    },
  });

  // Fetch creator players for matches where creator is not in the match
  const uniqueCreatorIds = Array.from(new Set(
    paginatedMatches
      .filter(match => match.createdByPlayerId)
      .map(match => {
        // Check if creator is in match
        const creatorInMatch =
          match.teamAPlayer1?.id === match.createdByPlayerId ||
          match.teamAPlayer2?.id === match.createdByPlayerId ||
          match.teamBPlayer1?.id === match.createdByPlayerId ||
          match.teamBPlayer2?.id === match.createdByPlayerId;
        return creatorInMatch ? null : match.createdByPlayerId;
      })
      .filter(Boolean) as string[]
  ));

  // Fetch all unique creator players at once using bulk endpoint
  const { data: creatorPlayers = [] } = useQuery<Player[]>({
    queryKey: ["/api/players/bulk", uniqueCreatorIds],
    queryFn: async () => {
      if (uniqueCreatorIds.length === 0) return [];
      const params = new URLSearchParams();
      uniqueCreatorIds.forEach(id => params.append('ids', id));
      const response = await apiRequest("GET", `/api/players/bulk?${params.toString()}`);
      return response.json();
    },
    enabled: uniqueCreatorIds.length > 0,
  });

  // Create a map for quick lookup
  const creatorPlayersMap = new Map(creatorPlayers.map(p => [p.id, p]));



  const getMatchResult = (match: MatchWithPlayersAndClub, playerId?: string): "won" | "lost" | "unknown" => {
    if (!playerId || !match.teamAPlayer1 || !match.teamAPlayer2 || !match.teamBPlayer1 || !match.teamBPlayer2) {
      return "unknown";
    }

    const isTeamA = match.teamAPlayer1.id === playerId || match.teamAPlayer2.id === playerId;
    const isTeamB = match.teamBPlayer1.id === playerId || match.teamBPlayer2.id === playerId;

    if (!isTeamA && !isTeamB) return "unknown";

    if ((match.winnerId === "A" || match.winnerId === "team_a") && isTeamA) return "won";
    if ((match.winnerId === "B" || match.winnerId === "team_b") && isTeamB) return "won";
    return "lost";
  };

  // Component to display match ELO change in header
  const MatchEloIndicator = ({ match }: { match: MatchWithPlayersAndClub }) => {
    // Use the main query results instead of individual queries
    const matchResults = allMatchResults.filter(result => result.matchId === match.id);

    return (
      <div className="flex items-center gap-2">
        <Check className="w-3 h-3 text-blue-800 dark-text-active" />
        <span className="text-xs font-medium text-blue-900 dark-text-active">MATCH</span>
      </div>
    );
  };

  // Extract last names only
  const getLastName = (fullName: string) => {
    const parts = fullName.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : fullName;
  };

  // Calculate team ELO (average of both players)
  const getTeamElo = (player1: Player, player2: Player) => {
    return Math.round((player1.eloRating + player2.eloRating) / 2);
  };

  // Get ELO change for a specific team
  const getTeamEloChange = (match: MatchWithPlayersAndClub, teamId: "A" | "B", matchResults: MatchResult[]) => {
    const teamPlayerIds = teamId === "A"
      ? [match.teamAPlayer1?.id, match.teamAPlayer2?.id]
      : [match.teamBPlayer1?.id, match.teamBPlayer2?.id];

    // Debug: Log what we're looking for
    console.log(`Looking for team ${teamId} players:`, teamPlayerIds);
    console.log(`Available match results:`, matchResults);

    // Find any player result from this team to get the ELO change
    const teamResult = matchResults.find(result =>
      teamPlayerIds.includes(result.playerId)
    );

    if (teamResult) {
      // Use club ELO if available, otherwise global ELO
      const eloChange = teamResult.clubEloChange !== null ? teamResult.clubEloChange : teamResult.eloChange;
      console.log(`Found team result for ${teamId}:`, { teamResult, eloChange });
      return typeof eloChange === 'number' ? eloChange : 0;
    }

    // If no match results found, return 0
    console.warn(`No match results found for team ${teamId} in match ${match.id}. Team player IDs:`, teamPlayerIds);
    return 0;
  };

  // Helper function to get historical ELO for a player at the time of the match
  const getHistoricalElo = (matchId: string, playerId: string, match: MatchWithPlayersAndClub): number | null => {
    const matchResults = allMatchResults.filter(result => result.matchId === matchId);
    const playerResult = matchResults.find(r => r.playerId === playerId);


    if (!playerResult) {
      // Fallback: return current ELO if no historical data
      const currentPlayer = [match.teamAPlayer1, match.teamAPlayer2, match.teamBPlayer1, match.teamBPlayer2]
        .find(p => p?.id === playerId);
      if (currentPlayer) {
        return currentPlayer.eloRating;
      }
      return null;
    }

    // Check if this match has a club and if club ELO data is available
    const hasClubElo = match?.clubId &&
      playerResult.clubEloChange !== null &&
      playerResult.newClubEloRating !== null &&
      typeof playerResult.clubEloChange === 'number' &&
      typeof playerResult.newClubEloRating === 'number';



    if (hasClubElo) {
      // Use club ELO: historical = new - change (ELO BEFORE the game)
      const historicalClubElo = playerResult.newClubEloRating! - playerResult.clubEloChange!;

      return historicalClubElo;
    } else {
      // Use global ELO: historical = new - change (ELO BEFORE the game)
      if (typeof playerResult.eloChange === 'number' && typeof playerResult.newEloRating === 'number') {
        const historicalGlobalElo = playerResult.newEloRating - playerResult.eloChange;

        return historicalGlobalElo;
      } else {
        // Fallback: return current ELO if no historical data
        const currentPlayer = [match.teamAPlayer1, match.teamAPlayer2, match.teamBPlayer1, match.teamBPlayer2]
          .find(p => p?.id === playerId);
        if (currentPlayer) {
          return currentPlayer.eloRating;
        }
      }
    }

    return null;
  };

  // Helper function to get historical team ELO (average of both players' historical ELO)
  const getHistoricalTeamElo = (matchId: string, player1: Player, player2: Player, match: MatchWithPlayersAndClub): number | null => {
    const player1Historical = getHistoricalElo(matchId, player1.id, match);
    const player2Historical = getHistoricalElo(matchId, player2.id, match);

    if (player1Historical === null || player2Historical === null) {
      // Fallback: calculate team ELO from current ratings
      const currentTeamElo = Math.round((player1.eloRating + player2.eloRating) / 2);
      return currentTeamElo;
    }

    return Math.round((player1Historical + player2Historical) / 2);
  };

  // Helper function to determine if we should use club ELO for display
  const shouldUseClubElo = (match: MatchWithPlayersAndClub): boolean => {
    if (!match.clubId) return false;

    // Check if any player has club ELO data
    const matchResults = allMatchResults.filter(result => result.matchId === match.id);
    return matchResults.some(result =>
      result.clubEloChange !== null &&
      result.newClubEloRating !== null &&
      typeof result.clubEloChange === 'number' &&
      typeof result.newClubEloRating === 'number'
    );
  };

  // Component to display ELO changes and historical ELO for a specific match
  const PlayerEloInfo = ({ matchId, player, match }: { matchId: string; player: Player; match: MatchWithPlayersAndClub }) => {
    // Use the main query results instead of individual queries
    const matchResults = allMatchResults.filter(result => result.matchId === matchId);

    const playerResult = matchResults.find(r => r.playerId === player.id);
    if (!playerResult) return <span className="text-xs text-gray-400">- / -</span>;

    // Use club ELO if this match is linked to a club and club ELO data is available
    const hasClubElo = match?.clubId &&
      playerResult.clubEloChange !== null &&
      playerResult.newClubEloRating !== null &&
      typeof playerResult.clubEloChange === 'number' &&
      typeof playerResult.newClubEloRating === 'number';

    const eloChange = hasClubElo ? playerResult.clubEloChange : playerResult.eloChange;
    const currentElo = hasClubElo ? playerResult.newClubEloRating : playerResult.newEloRating;

    // Add safety checks for numbers
    if (typeof eloChange !== 'number' || typeof currentElo !== 'number') {
      return <span className="text-xs text-gray-400">- / -</span>;
    }

    const historicalElo = currentElo - eloChange; // ELO before the match
    const isPositive = eloChange > 0;

    return (
      <span className="text-xs flex items-center gap-1">
        {hasClubElo && <img src="/tennis-ball.png" alt="Club" className="w-3 h-3" />}
        <span className="text-gray-600">{historicalElo}</span>
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-9 w-full mb-3" />
        <div className="flex space-x-6 mb-4">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-12" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  const filterTabs: { id: HistoryFilter; label: string }[] = [
    { id: "mine", label: t('history.filter.mine', 'Mes parties') },
    { id: "clubs", label: t('history.filter.clubs', 'Mes clubs') },
    { id: "all", label: t('history.filter.all', 'Toutes') },
  ];

  return (
    <div data-testid="history-tab">
      <section className="p-4">
        {/* Search Bar */}
        <div className="relative mb-3" id="tour-history-search">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            type="text"
            placeholder={t('history.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            className="pl-10 pr-8 bg-gray-50 dark:bg-muted border-transparent focus-visible:ring-1 focus-visible:bg-white dark:focus-visible:bg-input text-xs placeholder:text-gray-400 dark:placeholder:text-muted-foreground h-9"
            data-testid="search-history-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
          {isSearchFocused && !searchQuery && (
            <div className="absolute top-full left-0 mt-1 z-20 bg-padel-green text-white text-xs rounded-lg px-3 py-2 shadow-lg w-full">
              <p className="text-white/80 mb-1">{t('history.searchTooltipTitle')}</p>
              <p className="font-bold">{t('history.searchTooltipAnd')}</p>
              <p className="font-bold mt-0.5">{t('history.searchTooltipOr')}</p>
            </div>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex items-end justify-between mb-4">
          <div className="flex space-x-6 text-sm font-medium text-gray-500 overflow-x-auto hide-scrollbar">
            {filterTabs.map(tab => {
              const disabled = (tab.id === "mine" || tab.id === "clubs") && !playerId;
              return (
                <button
                  key={tab.id}
                  onClick={() => !disabled && setActiveFilter(tab.id)}
                  disabled={disabled}
                  className={`pb-3 whitespace-nowrap border-b-2 transition-colors ${
                    activeFilter === tab.id
                      ? "border-[#001489] text-[#001489] dark-text-active"
                      : disabled
                      ? "border-transparent text-gray-300 cursor-not-allowed"
                      : "border-transparent hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <span className="pb-3 text-xs text-gray-400 whitespace-nowrap">
            {filteredMatches.length + filteredTournaments.length} {filteredMatches.length + filteredTournaments.length === 1 ? t('history.counter.match', 'partie') : t('history.counter.matches', 'parties')}
          </span>
        </div>

        <div className="space-y-3" data-testid="match-history-list">
          {(() => {
            // Build merged list of matches + tournaments sorted by date desc
            type MatchEntry = { kind: "match"; item: typeof paginatedMatches[number]; index: number };
            type TournamentEntry = { kind: "tournament"; item: typeof filteredTournaments[number] };
            type Entry = MatchEntry | TournamentEntry;

            const matchEntries: Entry[] = paginatedMatches
              .filter(m => m.teamAPlayer1 && m.teamAPlayer2 && m.teamBPlayer1 && m.teamBPlayer2)
              .map((item, index) => ({ kind: "match" as const, item, index }));

            const tournamentEntries: Entry[] = filteredTournaments
              .map(item => ({ kind: "tournament" as const, item }));

            const combined = [...matchEntries, ...tournamentEntries].sort((a, b) => {
              const dateA = a.kind === "match"
                ? new Date(a.item.startTime).getTime()
                : new Date((a.item as TournamentEntry["item"]).completedAt ?? 0).getTime();
              const dateB = b.kind === "match"
                ? new Date(b.item.startTime).getTime()
                : new Date((b.item as TournamentEntry["item"]).completedAt ?? 0).getTime();
              return dateB - dateA;
            });

            if (combined.length === 0) {
              return (
                <div className="text-center text-gray-500 py-8" data-testid="no-matches">
                  {searchQuery ? t('history.noMatchesFound') : t('history.noMatchesYet')}
                </div>
              );
            }

            return (
              <>
                {combined.map((entry) => {
                  if (entry.kind === "tournament") {
                    const tour = entry.item;
                    const standings = [...tour.players].sort((a, b) => b.totalPoints - a.totalPoints);
                    const completedAt = tour.completedAt ? new Date(tour.completedAt) : null;
                    const formattedDate = completedAt
                      ? (differenceInHours(new Date(), completedAt) < 24
                        ? formatDistanceToNow(completedAt, { addSuffix: true })
                        : format(completedAt, 'MMM dd, HH:mm'))
                      : "";
                    return (
                      <Card key={tour.id} className="bg-white dark:bg-card border border-gray-200 dark:border-border overflow-hidden">
                        <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-b">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Check className="w-3 h-3 text-blue-800 dark-text-active" />
                              <span className="text-xs font-medium text-blue-900 dark-text-active">AMERICANO</span>
                              <span className="text-xs font-semibold text-gray-700 dark:text-foreground">{tour.name}</span>
                            </div>
                            {completedAt && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                {formattedDate}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Navigation dots */}
                        <div className="flex items-center justify-between px-2 py-1.5">
                          <button
                            onClick={() => setTournamentPages(p => { const i = p[tour.id] ?? -1; return { ...p, [tour.id]: i === -1 ? tour.rounds.length - 1 : i - 1 }; })}
                            disabled={tour.rounds.length === 0}
                            className="p-1 rounded disabled:opacity-20 hover:bg-gray-100 dark:hover:bg-muted"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setTournamentPages(p => ({ ...p, [tour.id]: -1 }))}
                              className={`h-1.5 rounded-full transition-all ${(tournamentPages[tour.id] ?? -1) === -1 ? "w-6 bg-[#001489]" : "w-1.5 bg-gray-300 dark:bg-gray-600"}`}
                            />
                            {tour.rounds.map((r, idx) => (
                              <button key={r.id} onClick={() => setTournamentPages(p => ({ ...p, [tour.id]: idx }))}
                                className={`h-1.5 rounded-full transition-all ${
                                  idx === (tournamentPages[tour.id] ?? -1) ? "w-6 bg-[#001489]"
                                  : r.matches.every(m => m.isCompleted) ? "w-1.5 bg-padel-green/60"
                                  : "w-1.5 bg-gray-200 dark:bg-gray-700"
                                }`}
                              />
                            ))}
                          </div>
                          <button
                            onClick={() => setTournamentPages(p => { const i = p[tour.id] ?? -1; return { ...p, [tour.id]: i === tour.rounds.length - 1 ? -1 : i + 1 }; })}
                            disabled={tour.rounds.length === 0}
                            className="p-1 rounded disabled:opacity-20 hover:bg-gray-100 dark:hover:bg-muted"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                        {/* Standings page */}
                        {(tournamentPages[tour.id] ?? -1) === -1 && (
                          <div className="px-3 pb-3 space-y-1">
                            {standings.map((ap, idx) => {
                              const isMe = ap.playerId === playerId;
                              const rank = idx + 1;
                              const bg = rank === 1 ? "#eab308" : rank === 2 ? "#9ca3af" : rank === 3 ? "#f97316" : "var(--muted)";
                              const color = rank <= 3 ? "#ffffff" : "var(--foreground)";
                              return (
                                <div key={ap.id} className={`flex items-center gap-2 rounded-lg px-2 py-1 text-xs ${isMe ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}>
                                  <div className="w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] border shrink-0"
                                    style={{ backgroundColor: bg, color, borderColor: bg }}>
                                    {rank}
                                  </div>
                                  <span className="flex-1 font-medium truncate">{ap.player?.name}</span>
                                  {ap.eloChange != null && (
                                    <div className={`flex items-center gap-1 ${ap.eloChange >= 0 ? "text-blue-800" : "text-red-600"}`}>
                                      <span className="font-medium">{ap.eloChange >= 0 ? `+${ap.eloChange}` : ap.eloChange}</span>
                                      {ap.eloChange >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                                    </div>
                                  )}
                                  <span className="font-bold w-12 text-right">{ap.totalPoints} pts</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Round page */}
                        {(tournamentPages[tour.id] ?? -1) >= 0 && (() => {
                          const round = tour.rounds[tournamentPages[tour.id] ?? 0];
                          if (!round) return null;
                          const getFirst = (name: string) => { const p = name.split(" "); return p.length > 1 ? p[p.length - 1] : p[0]; };
                          return (
                            <div className="px-3 pb-3 space-y-2">
                              {round.matches.every(m => m.isCompleted) && (
                                <div className="text-center pb-1"><CheckCircle className="inline h-3 w-3 text-padel-green" /></div>
                              )}
                              {round.matches.map((match) => {
                                const sA = match.scoreTeamA, sB = match.scoreTeamB;
                                const teamA = `${getFirst(match.teamALeftPlayer?.name ?? "")} / ${getFirst(match.teamARightPlayer?.name ?? "")}`;
                                const teamB = `${getFirst(match.teamBLeftPlayer?.name ?? "")} / ${getFirst(match.teamBRightPlayer?.name ?? "")}`;
                                return (
                                  <div key={match.id} className="rounded-xl overflow-hidden border border-gray-200 dark:border-border">
                                    <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-border bg-white dark:bg-card">
                                      <span className="font-medium text-sm truncate flex-1">{teamA}</span>
                                      <span className={`font-bold text-sm w-7 text-center ml-2 ${sA > sB ? "text-blue-800" : sA < sB ? "text-red-600" : "text-muted-foreground"}`}>{sA}</span>
                                    </div>
                                    <div className="flex items-center justify-between px-3 py-2.5 bg-white dark:bg-card">
                                      <span className="font-medium text-sm truncate flex-1">{teamB}</span>
                                      <span className={`font-bold text-sm w-7 text-center ml-2 ${sB > sA ? "text-blue-800" : sB < sA ? "text-red-600" : "text-muted-foreground"}`}>{sB}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-t">
                          <div className="flex justify-between items-center text-xs text-gray-600 dark:text-muted-foreground">
                            <div className="flex items-center gap-3">
                              {tour.startedAt && tour.completedAt && (() => {
                                const durationSec = Math.floor((new Date(tour.completedAt!).getTime() - new Date(tour.startedAt!).getTime()) / 1000);
                                const h = Math.floor(durationSec / 3600), m = Math.floor((durationSec % 3600) / 60), s = durationSec % 60;
                                return (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-gray-500" />
                                    {h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`}
                                  </span>
                                );
                              })()}
                              {tour.createdBy && (
                                <span className="flex items-center gap-1">
                                  <FileEdit className="w-3 h-3 text-gray-500" />
                                  {getLastName(tour.createdBy.name)}
                                </span>
                              )}
                              <span>{standings.length} joueurs · {tour.rounds?.length ?? 0} rounds</span>
                            </div>
                            {tour.club && (
                              <span className="flex items-center gap-1">
                                <img src="/tennis-ball.png" alt="Club" className="w-3 h-3 dark:invert" />
                                {tour.club.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  }

                  // Match card
                  const match = entry.item;
                  const matchIndex = entry.index;
                  const startTime = new Date(match.startTime);
                  const isWithin24Hours = differenceInHours(new Date(), startTime) < 24;
                  const formattedTime = isWithin24Hours
                    ? formatDistanceToNow(startTime, { addSuffix: true, includeSeconds: false })
                      .replace(/^about\s/, '').replace(/^almost\s/, '').replace(/^over\s/, '').replace(/^under\s/, '')
                    : format(startTime, 'MMM dd, HH:mm');
                  const matchResults = allMatchResults.filter(r => r.matchId === match.id);

                  return (
                    <Card key={match.id} className="bg-white dark:bg-card border border-gray-200 dark:border-border overflow-hidden" data-testid={`match-history-${match.id}`} id={matchIndex === 0 ? 'tour-history-list' : undefined}>
                      <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-b">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {match.isConfirmed ? (
                              <MatchEloIndicator match={match} />
                            ) : (
                              <>
                                <Clock className="w-3 h-3 text-yellow-600" />
                                <span className="text-xs font-medium text-yellow-700">{t('history.pending')}</span>
                              </>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            {formattedTime}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-border">
                          <div className="flex flex-col">
                            <div className="font-medium text-foreground">
                              {getLastName(match.teamAPlayer1.name)} ({getHistoricalElo(match.id, match.teamAPlayer1.id, match) || 'N/A'}) / {getLastName(match.teamAPlayer2.name)} ({getHistoricalElo(match.id, match.teamAPlayer2.id, match) || 'N/A'})
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {t('history.eloTeam')}: {getHistoricalTeamElo(match.id, match.teamAPlayer1, match.teamAPlayer2, match) || 'N/A'}
                              {shouldUseClubElo(match) && <span className="ml-2 text-padel-green">{t('history.clubElo')}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className={`flex items-center gap-1 ${(match.winnerId === "A" || match.winnerId === "team_a") ? 'text-blue-800' : 'text-red-600'}`}>
                              <span className="text-sm font-medium">
                                {(match.winnerId === "A" || match.winnerId === "team_a") ? `+${getTeamEloChange(match, "A", matchResults)}` : `-${Math.abs(getTeamEloChange(match, "A", matchResults))}`}
                              </span>
                              {(match.winnerId === "A" || match.winnerId === "team_a") ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            </div>
                            {match.teamAScore.map((score, i) => {
                              const teamBScore = match.teamBScore[i] || 0;
                              return <div key={i} className={`text-sm min-w-[24px] text-center ${score > teamBScore ? 'font-bold text-blue-800 dark-text-active' : 'text-gray-600 dark:text-gray-400'}`}>{score}</div>;
                            })}
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-3">
                          <div className="flex flex-col">
                            <div className="font-medium text-foreground">
                              {getLastName(match.teamBPlayer1.name)} ({getHistoricalElo(match.id, match.teamBPlayer1.id, match) || 'N/A'}) / {getLastName(match.teamBPlayer2.name)} ({getHistoricalElo(match.id, match.teamBPlayer2.id, match) || 'N/A'})
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {t('history.eloTeam')}: {getHistoricalTeamElo(match.id, match.teamBPlayer1, match.teamBPlayer2, match) || 'N/A'}
                              {shouldUseClubElo(match) && <span className="ml-2 text-padel-green">{t('history.clubElo')}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className={`flex items-center gap-1 ${(match.winnerId === "B" || match.winnerId === "team_b") ? 'text-blue-800' : 'text-red-600'}`}>
                              <span className="text-sm font-medium">
                                {(match.winnerId === "B" || match.winnerId === "team_b") ? `+${getTeamEloChange(match, "B", matchResults)}` : `-${Math.abs(getTeamEloChange(match, "B", matchResults))}`}
                              </span>
                              {(match.winnerId === "B" || match.winnerId === "team_b") ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            </div>
                            {match.teamBScore.map((score, i) => {
                              const teamAScore = match.teamAScore[i] || 0;
                              return <div key={i} className={`text-sm min-w-[24px] text-center ${score > teamAScore ? 'font-bold text-blue-800 dark-text-active' : 'text-gray-600 dark:text-gray-400'}`}>{score}</div>;
                            })}
                          </div>
                        </div>
                      </div>
                      {(match.club || match.duration || match.createdByPlayerId) && (
                        <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-t">
                          <div className="flex justify-between items-center text-xs text-gray-600">
                            <div className="flex items-center gap-3">
                              {(match.duration || (match.endTime && match.startTime)) && (() => {
                                let durationInSeconds = match.duration;
                                if (!durationInSeconds && match.endTime && match.startTime) {
                                  durationInSeconds = Math.floor((new Date(match.endTime).getTime() - new Date(match.startTime).getTime()) / 1000);
                                }
                                const finalDuration = Math.max(durationInSeconds || 0, 60);
                                const hours = Math.floor(finalDuration / 3600);
                                const minutes = Math.floor((finalDuration % 3600) / 60);
                                const seconds = finalDuration % 60;
                                return (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-gray-500" />
                                    {hours > 0 ? `${hours}:${minutes.toString().padStart(2,'0')}:${seconds.toString().padStart(2,'0')}` : `${minutes}:${seconds.toString().padStart(2,'0')}`}
                                  </span>
                                );
                              })()}
                              {match.createdByPlayerId && (() => {
                                const creatorInMatch =
                                  match.teamAPlayer1?.id === match.createdByPlayerId ? match.teamAPlayer1 :
                                  match.teamAPlayer2?.id === match.createdByPlayerId ? match.teamAPlayer2 :
                                  match.teamBPlayer1?.id === match.createdByPlayerId ? match.teamBPlayer1 :
                                  match.teamBPlayer2?.id === match.createdByPlayerId ? match.teamBPlayer2 : null;
                                const creator = creatorInMatch || creatorPlayersMap.get(match.createdByPlayerId);
                                const creatorName = creator ? getLastName(creator.name) : null;
                                return creatorName ? (
                                  <span className="flex items-center gap-1">
                                    <FileEdit className="w-3 h-3 text-gray-500" />
                                    {creatorName}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            {match.club && (
                              <span className="flex items-center gap-1">
                                <img src="/tennis-ball.png" alt="Club" className="w-3 h-3 dark:invert" />
                                {match.club.name}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
                {isLoadingMore && (
                  <div className="flex justify-center items-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">{t('history.loadingMore')}</span>
                  </div>
                )}
                {loadedCount < filteredMatches.length && <div ref={sentinelRef} className="h-1 w-full" />}
                {loadedCount >= filteredMatches.length && filteredMatches.length > ITEMS_PER_PAGE && (
                  <div className="text-center text-gray-500 py-4 text-sm">
                    {t('history.allLoaded', { count: filteredMatches.length })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </section>
    </div>
  );
}
